import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Pencil, X, Save, AlertCircle, Mail, Phone } from "lucide-react";
import { fetchProperty, updateProperty } from "../lib/api";
import { fmtEur, fmtQm, fmtPct, fmtDec, fmtDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";

// ---------------------------------------------------------------------------
// View helpers
// ---------------------------------------------------------------------------
const Field = ({ label, children, testid }) => (
  <div data-testid={testid} className="space-y-1">
    <div className="pb-row-label">{label}</div>
    <div className="pb-row-value">{children ?? "—"}</div>
  </div>
);

const SectionTitle = ({ children, eyebrow, testid }) => (
  <div className="mb-5" data-testid={testid}>
    {eyebrow && <div className="pb-eyebrow mb-1">{eyebrow}</div>}
    <h2 className="font-serif text-2xl text-navy border-b border-navy/15 pb-2">
      {children}
    </h2>
  </div>
);

// ---------------------------------------------------------------------------
// Input helpers (edit mode)
// ---------------------------------------------------------------------------
const Input = ({ label, value, onChange, type = "text", testid, suffix }) => (
  <label className="block" data-testid={testid ? `${testid}-wrap` : undefined}>
    <span className="pb-label">{label}</span>
    <div className="flex items-baseline gap-2">
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            type === "number"
              ? e.target.value === ""
                ? ""
                : Number(e.target.value)
              : e.target.value,
          )
        }
        className="pb-input--full"
      />
      {suffix && <span className="text-xs text-navy/55 font-sans">{suffix}</span>}
    </div>
  </label>
);

const Textarea = ({ label, value, onChange, testid, rows = 3 }) => (
  <label className="block" data-testid={testid ? `${testid}-wrap` : undefined}>
    <span className="pb-label">{label}</span>
    <textarea
      data-testid={testid}
      value={value ?? ""}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className="pb-input--full resize-y"
    />
  </label>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Objekt() {
  const [original, setOriginal] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load
  useEffect(() => {
    let active = true;
    fetchProperty()
      .then((p) => {
        if (!active) return;
        setOriginal(p);
        setData(structuredClone(p));
      })
      .catch((e) => active && setErr(e?.message || "Laden fehlgeschlagen"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const set = (key) => (value) => setData((d) => ({ ...d, [key]: value }));

  const setMieter = (idx, key) => (value) =>
    setData((d) => {
      const next = structuredClone(d);
      next.mieter[idx][key] = value;
      return next;
    });

  const setGebaeude = (idx, key) => (value) =>
    setData((d) => {
      const next = structuredClone(d);
      next.gebaeude[idx][key] = value;
      return next;
    });

  const setKontakt = (idx, key) => (value) =>
    setData((d) => {
      const next = structuredClone(d);
      next.kontakte[idx][key] = value;
      return next;
    });

  // KPIs / Summen (View)
  const totals = useMemo(() => {
    if (!data) return null;
    const miete = data.mieter.reduce((s, m) => s + (Number(m.miete_pa_eur) || 0), 0);
    const flaeche = data.mieter.reduce((s, m) => s + (Number(m.flaeche_bgf_qm) || 0), 0);
    return { miete, flaeche };
  }, [data]);

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      // Saubere Payload (alle relevanten Felder, Mieter mit IDs)
      const payload = {
        name: data.name,
        adresse_strasse: data.adresse_strasse,
        adresse_plz: data.adresse_plz,
        adresse_ort: data.adresse_ort,
        land: data.land,
        objektart: data.objektart,
        gebaeude: data.gebaeude.map((g) => ({
          name: g.name,
          bgf_qm: Number(g.bgf_qm) || 0,
        })),
        bgf_gesamt_qm: Number(data.bgf_gesamt_qm) || 0,
        mietflaeche_qm: Number(data.mietflaeche_qm) || 0,
        grundstueck_qm: Number(data.grundstueck_qm) || 0,
        erweiterungspotenzial_bgf_qm: Number(data.erweiterungspotenzial_bgf_qm) || 0,
        erbbau_laufzeit_bis: Number(data.erbbau_laufzeit_bis) || 0,
        erbbau_heimfall_text: data.erbbau_heimfall_text,
        erbbau_beleihungsgrenze_eur: Number(data.erbbau_beleihungsgrenze_eur) || 0,
        erbbau_sonderkuendigung_jahr: Number(data.erbbau_sonderkuendigung_jahr) || 0,
        mieter: data.mieter.map((m) => ({
          id: m.id,
          name: m.name,
          miete_anteil_prozent: Number(m.miete_anteil_prozent) || 0,
          miete_pa_eur: Number(m.miete_pa_eur) || 0,
          flaeche_bgf_qm: Number(m.flaeche_bgf_qm) || 0,
          eur_pro_qm: Number(m.eur_pro_qm) || 0,
          wault_text: m.wault_text,
        })),
        makler: data.makler,
        verkaeufer: data.verkaeufer,
        kontakte: data.kontakte.map((k) => ({
          name: k.name,
          email: k.email,
          telefon: k.telefon,
        })),
        indikative_gebote_termin: data.indikative_gebote_termin,
        bnp_report_nummer: Number(data.bnp_report_nummer) || 0,
        bnp_report_text: data.bnp_report_text,
        investoren_angesprochen: Number(data.investoren_angesprochen) || 0,
        ndas_versandt: Number(data.ndas_versandt) || 0,
        ndas_unterzeichnet: Number(data.ndas_unterzeichnet) || 0,
        indikative_gebote_anzahl: Number(data.indikative_gebote_anzahl) || 0,
        notizen: data.notizen || "",
      };
      const updated = await updateProperty(payload);
      setOriginal(updated);
      setData(structuredClone(updated));
      setEditMode(false);
      toast.success("Objektdaten gespeichert.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Speichern fehlgeschlagen";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setData(structuredClone(original));
    setEditMode(false);
  };

  if (loading) {
    return (
      <div className="py-24 text-center text-navy/50 font-sans" data-testid="objekt-loading">
        Lade Objektdaten …
      </div>
    );
  }
  if (err || !data) {
    return (
      <div
        className="pb-card flex items-center gap-3 text-navy/80"
        data-testid="objekt-error"
      >
        <AlertCircle size={18} className="text-gold" />
        Objektdaten konnten nicht geladen werden.
      </div>
    );
  }

  return (
    <div data-testid="objekt-page" className="space-y-10 pb-4">
      {/* Hero / Toolbar */}
      <PageHeader
        eyebrow="Newton Campus · Objekt-Stammdaten"
        title={original.name}
        subtitle={`${original.adresse_strasse} · ${original.adresse_plz} ${original.adresse_ort} · ${original.land}`}
        testid="objekt-header"
        actions={
          !editMode ? (
            <button
              type="button"
              data-testid="objekt-edit-btn"
              onClick={() => setEditMode(true)}
              className="pb-btn-accent text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2"
            >
              <Pencil size={13} strokeWidth={1.8} /> Bearbeiten
            </button>
          ) : (
            <>
              <button
                type="button"
                data-testid="objekt-cancel-btn"
                onClick={handleCancel}
                className="pb-btn-outline text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2"
                disabled={saving}
              >
                <X size={13} strokeWidth={1.8} /> Abbrechen
              </button>
              <button
                type="button"
                data-testid="objekt-save-btn"
                onClick={handleSave}
                className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2"
                disabled={saving}
              >
                <Save size={13} strokeWidth={1.8} />
                {saving ? "Speichert …" : "Speichern"}
              </button>
            </>
          )
        }
      />

      {editMode && (
        <div
          data-testid="objekt-edit-banner"
          className="border-l-[3px] border-l-gold bg-ledger px-4 py-3 text-sm text-navy font-sans"
        >
          Bearbeitungsmodus aktiv — Änderungen werden erst mit „Speichern" in die Datenbank geschrieben.
        </div>
      )}

      {/* ===================== ALLGEMEIN ===================== */}
      <section data-testid="section-allgemein">
        <SectionTitle eyebrow="01 · Allgemein">Allgemein</SectionTitle>

        {!editMode ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-5">
            <Field label="Objektname" testid="field-name">{data.name}</Field>
            <Field label="Adresse" testid="field-adresse">
              {data.adresse_strasse}, {data.adresse_plz} {data.adresse_ort}
            </Field>
            <Field label="Land" testid="field-land">{data.land}</Field>
            <Field label="Objektart" testid="field-objektart">{data.objektart}</Field>
            <Field label="Anzahl Gebäude" testid="field-gebaeude-anzahl">
              {data.gebaeude.length}
            </Field>
            <Field label="Gebäudenamen" testid="field-gebaeude-namen">
              {data.gebaeude.map((g) => g.name).join(", ")}
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            <Input label="Objektname" value={data.name} onChange={set("name")} testid="input-name" />
            <Input label="Straße" value={data.adresse_strasse} onChange={set("adresse_strasse")} testid="input-strasse" />
            <Input label="PLZ" value={data.adresse_plz} onChange={set("adresse_plz")} testid="input-plz" />
            <Input label="Ort" value={data.adresse_ort} onChange={set("adresse_ort")} testid="input-ort" />
            <Input label="Land" value={data.land} onChange={set("land")} testid="input-land" />
            <Input label="Objektart" value={data.objektart} onChange={set("objektart")} testid="input-objektart" />
          </div>
        )}

        {/* Gebäude-Tabelle */}
        <div className="mt-8">
          <div className="pb-eyebrow mb-3">Gebäude (BGF)</div>
          <table className="pb-table" data-testid="table-gebaeude">
            <thead>
              <tr>
                <th>Gebäude</th>
                <th className="text-right">BGF (m²)</th>
              </tr>
            </thead>
            <tbody>
              {data.gebaeude.map((g, idx) => (
                <tr key={g.name + idx}>
                  <td>
                    {editMode ? (
                      <input
                        data-testid={`input-gebaeude-${idx}-name`}
                        className="pb-input"
                        value={g.name}
                        onChange={(e) => setGebaeude(idx, "name")(e.target.value)}
                      />
                    ) : (
                      g.name
                    )}
                  </td>
                  <td className="text-right">
                    {editMode ? (
                      <input
                        data-testid={`input-gebaeude-${idx}-bgf`}
                        type="number"
                        className="pb-input text-right"
                        value={g.bgf_qm}
                        onChange={(e) =>
                          setGebaeude(idx, "bgf_qm")(Number(e.target.value))
                        }
                      />
                    ) : (
                      fmtQm(g.bgf_qm)
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-ledger">
                <td className="font-medium text-navy">Summe</td>
                <td className="text-right font-medium text-navy">
                  {fmtQm(data.gebaeude.reduce((s, g) => s + (Number(g.bgf_qm) || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ===================== FLÄCHEN ===================== */}
      <section data-testid="section-flaechen">
        <SectionTitle eyebrow="02 · Flächen">Flächen</SectionTitle>
        {!editMode ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-6">
            <Field label="BGF gesamt" testid="field-bgf">{fmtQm(data.bgf_gesamt_qm)}</Field>
            <Field label="Mietfläche" testid="field-mietflaeche">{fmtQm(data.mietflaeche_qm)}</Field>
            <Field label="Grundstück" testid="field-grundstueck">{fmtQm(data.grundstueck_qm)}</Field>
            <Field label="Erweiterungspotenzial (BGF)" testid="field-erweiterung">
              ~ {fmtQm(data.erweiterungspotenzial_bgf_qm)}
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
            <Input label="BGF gesamt (m²)" type="number" value={data.bgf_gesamt_qm} onChange={set("bgf_gesamt_qm")} testid="input-bgf" />
            <Input label="Mietfläche (m²)" type="number" value={data.mietflaeche_qm} onChange={set("mietflaeche_qm")} testid="input-mietflaeche" />
            <Input label="Grundstück (m²)" type="number" value={data.grundstueck_qm} onChange={set("grundstueck_qm")} testid="input-grundstueck" />
            <Input label="Erweiterung BGF (m²)" type="number" value={data.erweiterungspotenzial_bgf_qm} onChange={set("erweiterungspotenzial_bgf_qm")} testid="input-erweiterung" />
          </div>
        )}
      </section>

      {/* ===================== ERBBAU ===================== */}
      <section data-testid="section-erbbau">
        <SectionTitle eyebrow="03 · Erbbaurecht">Erbbaurecht</SectionTitle>
        {!editMode ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-6">
            <Field label="Laufzeit bis" testid="field-erbbau-laufzeit">{data.erbbau_laufzeit_bis}</Field>
            <Field label="Heimfall" testid="field-erbbau-heimfall">{data.erbbau_heimfall_text}</Field>
            <Field label="Beleihungsgrenze" testid="field-erbbau-beleihung">{fmtEur(data.erbbau_beleihungsgrenze_eur)}</Field>
            <Field label="Sonderkündigung Erbbaugeber" testid="field-erbbau-sonderkuendigung">
              {data.erbbau_sonderkuendigung_jahr}
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-5">
            <Input label="Laufzeit bis (Jahr)" type="number" value={data.erbbau_laufzeit_bis} onChange={set("erbbau_laufzeit_bis")} testid="input-erbbau-laufzeit" />
            <Input label="Heimfall" value={data.erbbau_heimfall_text} onChange={set("erbbau_heimfall_text")} testid="input-erbbau-heimfall" />
            <Input label="Beleihungsgrenze (EUR)" type="number" value={data.erbbau_beleihungsgrenze_eur} onChange={set("erbbau_beleihungsgrenze_eur")} testid="input-erbbau-beleihung" />
            <Input label="Sonderkündigung (Jahr)" type="number" value={data.erbbau_sonderkuendigung_jahr} onChange={set("erbbau_sonderkuendigung_jahr")} testid="input-erbbau-sonderkuendigung" />
          </div>
        )}
      </section>

      {/* ===================== MIETER ===================== */}
      <section data-testid="section-mieter">
        <SectionTitle eyebrow="04 · Mieter">Mieter</SectionTitle>
        <table className="pb-table" data-testid="table-mieter">
          <thead>
            <tr>
              <th>Mieter</th>
              <th className="text-right">Anteil Miete</th>
              <th className="text-right">Miete p. a.</th>
              <th className="text-right">Fläche BGF</th>
              <th className="text-right">€/m²</th>
              <th>WAULT (Fest + Optionen)</th>
            </tr>
          </thead>
          <tbody>
            {data.mieter.map((m, idx) => (
              <tr key={m.id || idx} data-testid={`row-mieter-${idx}`}>
                <td>
                  {editMode ? (
                    <input className="pb-input" value={m.name}
                      data-testid={`input-mieter-${idx}-name`}
                      onChange={(e) => setMieter(idx, "name")(e.target.value)} />
                  ) : m.name}
                </td>
                <td className="text-right">
                  {editMode ? (
                    <input className="pb-input text-right" type="number"
                      data-testid={`input-mieter-${idx}-anteil`}
                      value={m.miete_anteil_prozent}
                      onChange={(e) => setMieter(idx, "miete_anteil_prozent")(Number(e.target.value))} />
                  ) : fmtPct(m.miete_anteil_prozent)}
                </td>
                <td className="text-right">
                  {editMode ? (
                    <input className="pb-input text-right" type="number"
                      data-testid={`input-mieter-${idx}-miete`}
                      value={m.miete_pa_eur}
                      onChange={(e) => setMieter(idx, "miete_pa_eur")(Number(e.target.value))} />
                  ) : fmtEur(m.miete_pa_eur)}
                </td>
                <td className="text-right">
                  {editMode ? (
                    <input className="pb-input text-right" type="number"
                      data-testid={`input-mieter-${idx}-flaeche`}
                      value={m.flaeche_bgf_qm}
                      onChange={(e) => setMieter(idx, "flaeche_bgf_qm")(Number(e.target.value))} />
                  ) : fmtQm(m.flaeche_bgf_qm)}
                </td>
                <td className="text-right">
                  {editMode ? (
                    <input className="pb-input text-right" type="number" step="0.01"
                      data-testid={`input-mieter-${idx}-eurqm`}
                      value={m.eur_pro_qm}
                      onChange={(e) => setMieter(idx, "eur_pro_qm")(Number(e.target.value))} />
                  ) : fmtDec(m.eur_pro_qm)}
                </td>
                <td>
                  {editMode ? (
                    <input className="pb-input" value={m.wault_text}
                      data-testid={`input-mieter-${idx}-wault`}
                      onChange={(e) => setMieter(idx, "wault_text")(e.target.value)} />
                  ) : m.wault_text}
                </td>
              </tr>
            ))}
            <tr className="bg-ledger">
              <td className="font-medium text-navy">Summe</td>
              <td className="text-right font-medium text-navy">
                {fmtPct(data.mieter.reduce((s, m) => s + (Number(m.miete_anteil_prozent) || 0), 0))}
              </td>
              <td className="text-right font-medium text-navy">{fmtEur(totals?.miete)}</td>
              <td className="text-right font-medium text-navy">{fmtQm(totals?.flaeche)}</td>
              <td className="text-right text-navy/60">—</td>
              <td className="text-navy/60">—</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* ===================== MANDAT / MAKLER ===================== */}
      <section data-testid="section-mandat">
        <SectionTitle eyebrow="05 · Mandat & Makler">Mandat / Makler</SectionTitle>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
          {!editMode ? (
            <>
              <Field label="Makler / Vermarkter" testid="field-makler">{data.makler}</Field>
              <Field label="Verkäufer" testid="field-verkaeufer">{data.verkaeufer}</Field>
            </>
          ) : (
            <>
              <Input label="Makler / Vermarkter" value={data.makler} onChange={set("makler")} testid="input-makler" />
              <Input label="Verkäufer" value={data.verkaeufer} onChange={set("verkaeufer")} testid="input-verkaeufer" />
            </>
          )}
        </div>

        <div className="mt-8">
          <div className="pb-eyebrow mb-3">Kontakte Verkäufer</div>
          <table className="pb-table" data-testid="table-kontakte">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Telefon</th>
              </tr>
            </thead>
            <tbody>
              {data.kontakte.map((k, idx) => (
                <tr key={k.email + idx} data-testid={`row-kontakt-${idx}`}>
                  <td>
                    {editMode ? (
                      <input className="pb-input" value={k.name}
                        data-testid={`input-kontakt-${idx}-name`}
                        onChange={(e) => setKontakt(idx, "name")(e.target.value)} />
                    ) : k.name}
                  </td>
                  <td>
                    {editMode ? (
                      <input className="pb-input" value={k.email}
                        data-testid={`input-kontakt-${idx}-email`}
                        onChange={(e) => setKontakt(idx, "email")(e.target.value)} />
                    ) : (
                      <a href={`mailto:${k.email}`} className="text-navy hover:text-gold inline-flex items-center gap-1.5">
                        <Mail size={13} strokeWidth={1.6} /> {k.email}
                      </a>
                    )}
                  </td>
                  <td>
                    {editMode ? (
                      <input className="pb-input" value={k.telefon}
                        data-testid={`input-kontakt-${idx}-telefon`}
                        onChange={(e) => setKontakt(idx, "telefon")(e.target.value)} />
                    ) : (
                      <a href={`tel:${k.telefon.replace(/\s/g, "")}`} className="text-navy hover:text-gold inline-flex items-center gap-1.5">
                        <Phone size={13} strokeWidth={1.6} /> {k.telefon}
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ===================== TERMINE / STATUS ===================== */}
      <section data-testid="section-termine">
        <SectionTitle eyebrow="06 · Termine & Status">Wichtige Termine / Notizen</SectionTitle>
        {!editMode ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-6">
              <Field label="Abgabe indikative Gebote" testid="field-termin">
                {fmtDate(data.indikative_gebote_termin)}
              </Field>
              <Field label="Letzter BNP-Report" testid="field-bnp-report">
                #{data.bnp_report_nummer}
              </Field>
              <Field label="Investoren angesprochen" testid="field-investoren">
                {data.investoren_angesprochen}
              </Field>
              <Field label="NDAs (versandt / unterzeichnet)" testid="field-ndas">
                {data.ndas_versandt} / {data.ndas_unterzeichnet}
              </Field>
              <Field label="Indikative Gebote" testid="field-gebote">
                {data.indikative_gebote_anzahl}
              </Field>
            </div>
            <div className="mt-6 pb-card" data-testid="bnp-report-block">
              <div className="pb-eyebrow mb-2">BNP-Report Zusammenfassung</div>
              <p className="text-sm text-navy/80 font-sans leading-relaxed">
                {data.bnp_report_text}
              </p>
            </div>
            {data.notizen && (
              <div className="mt-6 pb-card" data-testid="notizen-block">
                <div className="pb-eyebrow mb-2">Notizen</div>
                <p className="text-sm text-navy/80 font-sans whitespace-pre-wrap">
                  {data.notizen}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            <Input label="Termin indikative Gebote (YYYY-MM-DD)" value={data.indikative_gebote_termin} onChange={set("indikative_gebote_termin")} testid="input-termin" />
            <Input label="BNP-Report Nr." type="number" value={data.bnp_report_nummer} onChange={set("bnp_report_nummer")} testid="input-bnp-nr" />
            <Input label="Investoren angesprochen" type="number" value={data.investoren_angesprochen} onChange={set("investoren_angesprochen")} testid="input-investoren" />
            <Input label="NDAs versandt" type="number" value={data.ndas_versandt} onChange={set("ndas_versandt")} testid="input-ndas-versandt" />
            <Input label="NDAs unterzeichnet" type="number" value={data.ndas_unterzeichnet} onChange={set("ndas_unterzeichnet")} testid="input-ndas-unterz" />
            <Input label="Indikative Gebote" type="number" value={data.indikative_gebote_anzahl} onChange={set("indikative_gebote_anzahl")} testid="input-gebote" />
            <div className="md:col-span-2 lg:col-span-3">
              <Textarea label="BNP-Report Zusammenfassung" rows={3} value={data.bnp_report_text} onChange={set("bnp_report_text")} testid="input-bnp-text" />
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <Textarea label="Notizen" rows={4} value={data.notizen || ""} onChange={set("notizen")} testid="input-notizen" />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
